import React from 'react';
import { cx } from 'class-variance-authority';
import Icon from '@ui/Icon';
import Typography from '@ui/Typography';
import { Modal } from '@ui/Modal';
import {
  useGuidedProjectCreationProgress,
  type ImportModalSetupSection,
  type ImportModalSetupStep,
} from './guidedProjectCreationProgress';
import styles from './ImportModalSetup.module.css';

export const IMPORT_MODAL_SETUP_STEPS = [
  'Analyzing your text',
  'Reading formatting',
  'Pulling section names',
  'Finding voice data',
  'Preparing project',
] as const;

export type { ImportModalSetupStep };

export type ImportModalSetupStatus = 'pending' | 'loading' | 'done';

const STATUS_TEXT_COLOR: Record<
  ImportModalSetupStatus,
  'primary' | 'secondary' | 'tertiary'
> = {
  pending: 'tertiary',
  loading: 'primary',
  done: 'secondary',
};

const getImportModalSetupStatus = (
  index: number,
  step: ImportModalSetupStep
): ImportModalSetupStatus => {
  if (step === 6 || index + 1 < step) return 'done';
  if (index + 1 === step) return 'loading';
  return 'pending';
};

export type ImportModalSetupProps = {
  id?: string;
  'data-e2e'?: string;
  open?: boolean;
  close: () => void;
  /** The project this setup run is populating — drives the SSE connection to `/api/guided-project-creation/:projectId/events`. */
  projectId: string;
  /**
   * Which setup phase is current. `1`–`5` mark that row as the active
   * (loading) step; `6` marks every row done. Omit to let real progress
   * from `projectId`'s events drive the steps — pass this only to force a
   * state for Storybook.
   */
  step?: ImportModalSetupStep;
  /** Called with a human-readable reason when setup fails. */
  onFail?: (reason: string) => void;
  /** Called with the created sections once setup finishes; the modal closes itself right after. */
  onDone?: (sections: ImportModalSetupSection[]) => void;
};

function SetupIndicator({ status }: { status: ImportModalSetupStatus }) {
  const isDone = status === 'done';

  return (
    <span className={styles.indicator} data-status={status} aria-hidden>
      <Icon
        type={isDone ? 'checkCircle' : 'circle'}
        fontSize="large"
        htmlColor={
          isDone ? 'var(--color-lavender-300)' : 'var(--color-gray-300)'
        }
        className={cx(styles.leaf, status === 'loading' && styles.pulse)}
      />
    </span>
  );
}

/**
 * "Setting up your project" — progress overlay after the import modal in
 * guided project creation. Built from the Figma `ImportModalSetUp` node;
 * composes `Modal` + `Typography` + `Icon`. No close control in the
 * design, so the popup is non-dismissable.
 */
function ImportModalSetup(props: ImportModalSetupProps) {
  const {
    id = 'import-modal-setup',
    'data-e2e': dataE2E,
    open,
    close,
    projectId,
    step: stepProp,
    onFail,
    onDone,
  } = props;

  const isStepControlled = stepProp !== undefined;
  const { step: progressStep, sections } = useGuidedProjectCreationProgress({
    projectId,
    active: !isStepControlled,
    onFail,
  });
  const step = isStepControlled ? stepProp : progressStep;

  React.useEffect(() => {
    if (isStepControlled || progressStep !== 6) return;
    setTimeout(() => {
      close();
      onDone?.(sections);
    }, 1000);
  }, [isStepControlled, progressStep, sections, onDone, close]);

  return (
    <Modal
      id={id}
      data-e2e={dataE2E ?? id}
      open={open}
      close={close}
      dismissable={false}
      size="sm"
    >
      <div className={styles.main} aria-busy={step < 6 || undefined}>
        <div className={styles.heading}>
          <span className={styles.sparkle}>
            <Icon type="stars03" fontSize="inherit" htmlColor="currentColor" />
          </span>
          <Typography variant="h3" component="span" bold>
            Setting up your project
          </Typography>
          <Typography variant="h4" component="span" color="tertiary">
            Once ready, you&rsquo;re free to make edits.
          </Typography>
        </div>
        <ol className={styles.steps} aria-label="Project setup progress">
          {IMPORT_MODAL_SETUP_STEPS.map((label, index) => {
            const status = getImportModalSetupStatus(index, step);

            return (
              <li
                key={label}
                className={styles.step}
                aria-current={status === 'loading' ? 'step' : undefined}
              >
                <SetupIndicator status={status} />
                <Typography
                  variant="h5"
                  component="span"
                  color={STATUS_TEXT_COLOR[status]}
                >
                  {label}
                </Typography>
              </li>
            );
          })}
        </ol>
      </div>
    </Modal>
  );
}

export default ImportModalSetup;
